import logger from '../utilities/logger'
import ConfigService, { tenant } from './ConfigService'
import createChapiClient from '../utilities/chapiClient'


async function DataService(auth) {
  const baseUrl = ConfigService.chapiBaseUrl() || ''
  const chapiClient = await createChapiClient(
    auth,
    baseUrl,
    tenant
  )

  /**
   * [GET] Fetches help articles from the configured URL
   * @returns {Promise<Object>} The help articles data
   * @throws {Error} Throws an error if the request fails or URL is not provided
   */
  async function getArticles() {
    const helpArticles = window.document.getElementById('helpArticles')
    if (helpArticles && helpArticles.href) {
      return fetch(helpArticles.href)
        .then(response => response.json())
        .catch(e => {
          logger.error('Get articles failed', e)
          throw e
        })
    }
    return logger.error('Get articles failed - url not provided')
  }

  /**
   * [GET] Fetches article data for a specific article ID and order ID
   * @param {string} articleId - The ID of the article to fetch
   * @param {string} orderId - The ID of the order associated with the article
   * @returns {Promise<Object>} The article data
   * @throws {Error} Throws an error if the request fails
   */
  async function getArticleData(articleId, orderId) {
    return chapiClient.getArticleData(articleId, orderId)
  }

  /**
   * [POST] Creates a support request with the provided order ID, reason, and body
   * @param {string} orderId - The ID of the order for which the support request is created
   * @param {string} reason - The reason for the support request
   * @param {Object} body - The body of the support request
   * @returns {Promise<Object>} The response from the support request creation
   * @throws {Error} Throws an error if the request fails
   */
  async function createSupportRequest(orderId, reason, body) {
    return chapiClient.createSupportRequest(orderId, reason, body)
      .catch(e => {
        logger.error('Create support request failed', e)
        // This needs to throw for the form to update
        throw new Error(`POST: Email contact failed! ${e}`)
      })
  }

  /**
   * [POST] Creates a query support request with the provided order ID, query type, and body
   * @param {string} orderId - The ID of the order from input field
   * @param {string} queryType - The type of the query from <select> dropdown
   * @param {Object} body - The body of the query support request from <textarea> input
   * @returns {Promise<Object>} The response from the query support request creation
   * @throws {Error} Throws an error if the request fails
   */
  async function createQuerySupportRequest(orderId, queryType, body) {
    return chapiClient.createQuerySupportRequest(orderId, queryType, body)
  }

  /**
   * [GET] Fetches a chat support token
   * @returns {Promise<string>} The chat support token
   * @throws {Error} Throws an error if the request fails
   */
  async function getChatSupportToken() {
    return chapiClient.getChatSupportToken()
  }

  /**
   * [GET] Fetches the content concern form
   * @param {Object} routeQueries - The query parameters for the request
   * @returns {Promise<Object>} The content concern form data
   * @throws {Error} Throws an error if the request fails
   */
  async function getContentConcernForm(routeQueries) {
    return chapiClient.getContentConcernForm(routeQueries)
  }

  /**
   * [POST] Reports a content concern with the provided form data
   * @param {Object} formData - The form data for reporting the content concern
   * @returns {Promise<Object>} The response from the content concern report
   * @throws {Error} Throws an error if the request fails
   */
  async function reportContentConcern(formData) {
    return chapiClient.reportContentConcern(formData)
  }

  /**
   * [GET] Fetches the footer content from the configured URL
   * @returns {Promise<Object>} The footer content data
   * @throws {Error} Throws an error if the request fails or URL is not provided
   */
  async function getFooterContent() {
    const footerContent = window.document.getElementById('footerContent')
    if (footerContent && footerContent.href) {
      return fetch(footerContent.href)
        .then(response => response.json())
        .catch(e => {
          logger.error('Get footer content failed', e)
          throw e
        })
    }
    return logger.error('Get footer content failed - url not provided')
  }

  return {
    getArticles,
    getArticleData,
    createSupportRequest,
    createQuerySupportRequest,
    getChatSupportToken,
    getContentConcernForm,
    reportContentConcern,
    getFooterContent
  }
}

export default DataService
